import React, { useMemo, useEffect, useRef } from "react";

/* ====== THEME-SPECIFIC SPECIAL EFFECTS - PIXAR/DISNEY LEVEL ====== */

/* Ocean Depths, massive bubbles, bioluminescent fish, undulating kelp, caustic light */
const OceanEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Caustic light rays from surface */}
    <div className="absolute inset-0" style={{
      background: `
        repeating-conic-gradient(from 0deg at 30% 10%, rgba(34,211,238,0.04) 0deg, transparent 15deg, rgba(34,211,238,0.02) 30deg),
        repeating-conic-gradient(from 45deg at 70% 20%, rgba(6,182,212,0.03) 0deg, transparent 20deg)
      `,
      animation: "caustic-shimmer 8s ease-in-out infinite",
    }} />
    {/* Rising bubbles, various sizes */}
    {Array.from({ length: 25 }, (_, i) => {
      const size = 4 + Math.random() * 18;
      return (
        <div key={i} className="absolute rounded-full" style={{
          width: size, height: size,
          bottom: `-${5 + Math.random() * 10}%`,
          left: `${Math.random() * 100}%`,
          background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.25), rgba(34,211,238,0.15), transparent)`,
          border: "1px solid rgba(34,211,238,0.2)",
          animation: `bubble-rise ${5 + Math.random() * 10}s ease-in-out ${Math.random() * 12}s infinite`,
        }} />
      );
    })}
    {/* Bioluminescent jellyfish */}
    {[15, 45, 75].map((x, i) => (
      <div key={`jelly-${i}`} className="absolute" style={{
        left: `${x}%`, top: `${30 + i * 20}%`,
        animation: `jellyfish-drift ${12 + i * 4}s ease-in-out ${i * 3}s infinite`,
      }}>
        <div className="relative">
          <div className="w-8 h-6 rounded-t-full" style={{
            background: `radial-gradient(ellipse, ${['rgba(167,139,250,0.4)', 'rgba(236,72,153,0.35)', 'rgba(34,211,238,0.4)'][i]}, transparent)`,
            boxShadow: `0 0 20px ${['rgba(167,139,250,0.3)', 'rgba(236,72,153,0.25)', 'rgba(34,211,238,0.3)'][i]}`,
            animation: `jelly-pulse ${2 + i * 0.5}s ease-in-out infinite`,
          }} />
          {[0, 1, 2].map(t => (
            <div key={t} className="absolute" style={{
              width: 1, height: 16 + t * 4,
              background: `linear-gradient(180deg, ${['rgba(167,139,250,0.3)', 'rgba(236,72,153,0.25)', 'rgba(34,211,238,0.3)'][i]}, transparent)`,
              left: `${30 + t * 20}%`, top: '100%',
              animation: `tentacle-sway ${3 + t}s ease-in-out ${t * 0.5}s infinite`,
            }} />
          ))}
        </div>
      </div>
    ))}
    {/* Animated wave bands */}
    <svg className="absolute bottom-0 left-0 w-full h-[30%]" preserveAspectRatio="none" viewBox="0 0 1440 200">
      <path fill="rgba(6,182,212,0.1)">
        <animate attributeName="d" dur="6s" repeatCount="indefinite"
          values="M0,100 Q360,50 720,100 Q1080,150 1440,100 L1440,200 L0,200Z;M0,130 Q360,80 720,130 Q1080,180 1440,130 L1440,200 L0,200Z;M0,100 Q360,50 720,100 Q1080,150 1440,100 L1440,200 L0,200Z" />
      </path>
      <path fill="rgba(14,165,233,0.07)">
        <animate attributeName="d" dur="8s" repeatCount="indefinite"
          values="M0,140 Q360,100 720,140 Q1080,180 1440,140 L1440,200 L0,200Z;M0,110 Q360,70 720,110 Q1080,150 1440,110 L1440,200 L0,200Z;M0,140 Q360,100 720,140 Q1080,180 1440,140 L1440,200 L0,200Z" />
      </path>
    </svg>
    {/* Coral reef glow at bottom */}
    <div className="absolute bottom-0 left-0 w-full h-[15%]" style={{
      background: "linear-gradient(0deg, rgba(236,72,153,0.12), rgba(251,146,60,0.08), transparent)",
    }} />
    {/* Swimming fish schools */}
    {Array.from({ length: 6 }, (_, i) => (
      <div key={`fish-${i}`} className="absolute" style={{
        width: 6, height: 3,
        background: ['#67E8F9', '#A78BFA', '#FB923C', '#34D399', '#FBBF24', '#F472B6'][i],
        borderRadius: '50% 20% 20% 50%',
        top: `${20 + Math.random() * 50}%`,
        left: '-3%',
        opacity: 0.5,
        boxShadow: `0 0 6px ${['#67E8F9', '#A78BFA', '#FB923C', '#34D399', '#FBBF24', '#F472B6'][i]}50`,
        animation: `fish-swim ${8 + Math.random() * 8}s linear ${i * 2.5}s infinite`,
      }} />
    ))}
  </div>
);

/* Ancient Egypt, towering pyramids, hieroglyph particles, scarab beetles, sunbeams */
const PyramidEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Desert heat shimmer */}
    <div className="absolute bottom-0 left-0 w-full h-[40%]" style={{
      background: "linear-gradient(0deg, rgba(217,119,6,0.15) 0%, rgba(245,158,11,0.08) 40%, transparent 100%)",
      animation: "heat-shimmer 4s ease-in-out infinite",
    }} />
    {/* Pyramid silhouettes, grander */}
    <svg className="absolute bottom-0 left-0 w-full h-[40%]" preserveAspectRatio="none" viewBox="0 0 1440 350">
      <defs>
        <linearGradient id="pyramidGlow" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(245,158,11,0.3)" />
          <stop offset="100%" stopColor="rgba(180,83,9,0.15)" />
        </linearGradient>
      </defs>
      <polygon points="100,350 300,50 500,350" fill="rgba(180,83,9,0.18)" />
      <polygon points="400,350 650,10 900,350" fill="url(#pyramidGlow)" />
      <polygon points="800,350 1000,80 1200,350" fill="rgba(180,83,9,0.15)" />
      <polygon points="1100,350 1250,120 1400,350" fill="rgba(146,64,14,0.12)" />
      <path d="M0,320 Q200,290 400,310 Q600,330 800,300 Q1000,270 1200,295 Q1400,320 1440,310 L1440,350 L0,350Z" fill="rgba(217,119,6,0.12)" />
    </svg>
    {/* Torch flames, more dramatic */}
    {[18, 38, 58, 78].map((x, i) => (
      <div key={i} className="absolute" style={{ left: `${x}%`, bottom: '30%' }}>
        <div className="w-4 h-12 rounded-full" style={{
          background: "linear-gradient(0deg, rgba(251,146,60,0.8), rgba(234,179,8,0.5), rgba(239,68,68,0.3), transparent)",
          animation: `torch-flicker ${1.2 + i * 0.2}s ease-in-out infinite`,
          boxShadow: "0 0 20px rgba(251,146,60,0.5), 0 0 40px rgba(245,158,11,0.3), 0 -10px 30px rgba(234,179,8,0.2)",
          filter: "blur(1px)",
        }} />
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-6 bg-amber-900/30 rounded-b" />
      </div>
    ))}
    {/* Floating hieroglyph particles */}
    {Array.from({ length: 10 }, (_, i) => {
      const glyphs = ['\u2625', '\u2641', '\u2600', '\u263D', '\u2605', '\u2B50', '\u25C6', '\u25B2', '\u2B22', '\u2726'];
      return (
        <div key={i} className="absolute text-amber-500/15 font-bold" style={{
          fontSize: `${10 + Math.random() * 14}px`,
          top: `${Math.random() * 70}%`, left: `${Math.random() * 100}%`,
          animation: `hieroglyph-float ${8 + Math.random() * 8}s ease-in-out ${Math.random() * 10}s infinite`,
        }}>{glyphs[i]}</div>
      );
    })}
    {/* Sand particles blown by wind */}
    {Array.from({ length: 20 }, (_, i) => (
      <div key={`sand-${i}`} className="absolute rounded-full" style={{
        width: `${1 + Math.random() * 2}px`, height: `${1 + Math.random() * 2}px`,
        background: "rgba(217,119,6,0.35)",
        bottom: `${Math.random() * 40}%`, left: `${Math.random() * 100}%`,
        animation: `sand-drift ${4 + Math.random() * 4}s linear ${Math.random() * 5}s infinite`,
      }} />
    ))}
  </div>
);

/* Enchanted Forest, dense fireflies, glowing mushrooms, floating pollen, fairy trails */
const ForestEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Tree silhouettes, lush canopy */}
    <svg className="absolute bottom-0 left-0 w-full h-[45%] opacity-[0.1]" preserveAspectRatio="none" viewBox="0 0 1440 400">
      <path d="M0,400 L30,220 L60,280 L90,160 L120,240 L160,100 L200,240 L240,180 L280,300 L320,120 L370,260 L420,80 L470,220 L510,400" fill="#22C55E" />
      <path d="M480,400 L530,240 L570,300 L620,130 L660,260 L710,80 L760,220 L800,280 L840,140 L890,260 L940,60 L990,240 L1030,400" fill="#16A34A" />
      <path d="M1010,400 L1050,200 L1100,280 L1140,100 L1180,230 L1220,150 L1260,250 L1300,70 L1340,210 L1380,290 L1420,130 L1440,200 L1440,400" fill="#15803D" />
    </svg>
    {/* Firefly swarm, larger, more magical */}
    {Array.from({ length: 30 }, (_, i) => {
      const size = 3 + Math.random() * 5;
      const color = i % 3 === 0 ? "#4ADE80" : i % 3 === 1 ? "#FDE68A" : "#67E8F9";
      return (
        <div key={i} className="absolute rounded-full" style={{
          width: size, height: size,
          top: `${15 + Math.random() * 65}%`, left: `${Math.random() * 100}%`,
          background: color,
          boxShadow: `0 0 ${size * 3}px ${color}, 0 0 ${size * 6}px ${color}60`,
          animation: `firefly-dance ${3 + Math.random() * 5}s ease-in-out ${Math.random() * 8}s infinite`,
        }} />
      );
    })}
    {/* Glowing mushrooms at bottom */}
    {[8, 25, 55, 72, 90].map((x, i) => (
      <div key={`mush-${i}`} className="absolute" style={{ left: `${x}%`, bottom: `${5 + Math.random() * 10}%` }}>
        <div className="relative">
          <div style={{
            width: `${8 + i * 2}px`, height: `${5 + i}px`,
            borderRadius: '50% 50% 0 0',
            background: `radial-gradient(ellipse at 50% 80%, ${['rgba(167,139,250,0.5)', 'rgba(52,211,153,0.5)', 'rgba(236,72,153,0.4)', 'rgba(34,211,238,0.5)', 'rgba(251,191,36,0.4)'][i]}, transparent)`,
            boxShadow: `0 0 15px ${['rgba(167,139,250,0.3)', 'rgba(52,211,153,0.3)', 'rgba(236,72,153,0.2)', 'rgba(34,211,238,0.3)', 'rgba(251,191,36,0.2)'][i]}`,
            animation: `mushroom-glow ${3 + i}s ease-in-out infinite`,
          }} />
          <div style={{ width: 2, height: 8, background: 'rgba(255,255,255,0.08)', margin: '0 auto', borderRadius: 1 }} />
        </div>
      </div>
    ))}
    {/* Floating pollen/fairy dust */}
    {Array.from({ length: 12 }, (_, i) => (
      <div key={`pollen-${i}`} className="absolute rounded-full" style={{
        width: 2, height: 2,
        background: "rgba(251,191,36,0.5)",
        boxShadow: "0 0 4px rgba(251,191,36,0.4)",
        top: `${Math.random() * 80}%`, left: `${Math.random() * 100}%`,
        animation: `pollen-float ${6 + Math.random() * 8}s ease-in-out ${Math.random() * 10}s infinite`,
      }} />
    ))}
    {/* Falling leaves */}
    {Array.from({ length: 10 }, (_, i) => (
      <div key={`leaf-${i}`} className="absolute" style={{
        top: '-5%', left: `${Math.random() * 100}%`,
        fontSize: `${10 + Math.random() * 8}px`,
        color: ['rgba(34,197,94,0.3)', 'rgba(234,179,8,0.25)', 'rgba(239,68,68,0.2)', 'rgba(251,146,60,0.25)'][i % 4],
        animation: `leaf-fall ${7 + Math.random() * 7}s linear ${Math.random() * 12}s infinite`,
      }}>&#127811;</div>
    ))}
  </div>
);

/* Arctic Aurora, vivid aurora bands, dense snowfall, ice crystal sparkle */
const AuroraEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Multi-layered aurora bands */}
    <div className="absolute top-0 left-0 w-full h-[60%]">
      <div className="absolute w-full h-full" style={{
        background: "linear-gradient(180deg, rgba(34,211,238,0.2) 0%, rgba(52,211,153,0.15) 25%, rgba(167,139,250,0.12) 50%, transparent 100%)",
        animation: "aurora-wave 10s ease-in-out infinite",
      }} />
      <div className="absolute w-full h-full" style={{
        background: "linear-gradient(180deg, rgba(167,139,250,0.15) 0%, rgba(236,72,153,0.12) 25%, rgba(34,211,238,0.1) 50%, transparent 100%)",
        animation: `aurora-wave 14s ease-in-out -4s infinite reverse`,
      }} />
      <div className="absolute w-full h-full" style={{
        background: "linear-gradient(180deg, rgba(52,211,153,0.12) 0%, rgba(34,211,238,0.15) 30%, rgba(59,130,246,0.08) 60%, transparent 100%)",
        animation: `aurora-wave 18s ease-in-out -8s infinite`,
      }} />
      {/* Curtain-like aurora streaks */}
      <svg className="absolute inset-0 w-full h-full opacity-30">
        <defs>
          <linearGradient id="auroraGrad1" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#A78BFA" stopOpacity="0.2" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
        </defs>
        <path d="M200,0 Q250,100 200,200 Q150,300 200,400" stroke="url(#auroraGrad1)" strokeWidth="40" fill="none" opacity="0.3">
          <animate attributeName="d" dur="6s" repeatCount="indefinite"
            values="M200,0 Q250,100 200,200 Q150,300 200,400;M200,0 Q150,100 200,200 Q250,300 200,400;M200,0 Q250,100 200,200 Q150,300 200,400" />
        </path>
      </svg>
    </div>
    {/* Dense snowfall */}
    {Array.from({ length: 40 }, (_, i) => {
      const size = 2 + Math.random() * 4;
      return (
        <div key={i} className="absolute rounded-full bg-white" style={{
          width: size, height: size,
          top: '-4%', left: `${Math.random() * 100}%`,
          opacity: 0.2 + Math.random() * 0.5,
          animation: `snowfall ${4 + Math.random() * 8}s linear ${Math.random() * 12}s infinite`,
        }} />
      );
    })}
    {/* Ice crystal sparkles */}
    {Array.from({ length: 15 }, (_, i) => (
      <div key={`ice-${i}`} className="absolute" style={{
        width: 4, height: 4,
        top: `${Math.random() * 80}%`, left: `${Math.random() * 100}%`,
        background: '#E0F2FE',
        clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        animation: `ice-sparkle ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 5}s infinite`,
        filter: 'drop-shadow(0 0 3px rgba(224,242,254,0.5))',
      }} />
    ))}
    {/* Ice mountain silhouette */}
    <svg className="absolute bottom-0 left-0 w-full h-[25%] opacity-[0.08]" preserveAspectRatio="none" viewBox="0 0 1440 200">
      <path d="M0,200 L80,130 L160,170 L300,40 L440,120 L580,50 L680,140 L820,20 L960,100 L1080,45 L1200,120 L1350,60 L1440,100 L1440,200Z" fill="#BAE6FD" />
    </svg>
  </div>
);

/* Candy Kingdom, bold rainbow, sprinkle storm, candy shapes, lollipop silhouettes */
const CandyEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Bold rainbow arc */}
    <div className="absolute top-[3%] left-1/2 -translate-x-1/2 w-[90%] h-[45%] rounded-t-full opacity-[0.18]" style={{
      background: "conic-gradient(from 180deg, #EF4444, #F97316, #EAB308, #22C55E, #3B82F6, #8B5CF6, #EC4899, transparent 55%)",
      animation: "rainbow-pulse 6s ease-in-out infinite",
      filter: "blur(8px)",
    }} />
    {/* Sprinkle rain, more dense */}
    {Array.from({ length: 25 }, (_, i) => {
      const colors = ["#EC4899", "#FBBF24", "#34D399", "#3B82F6", "#A78BFA", "#FB923C", "#EF4444"];
      return (
        <div key={i} className="absolute rounded-sm" style={{
          width: `${2 + Math.random() * 2}px`, height: `${6 + Math.random() * 6}px`,
          background: colors[i % colors.length],
          top: '-4%', left: `${Math.random() * 100}%`,
          opacity: 0.5,
          transform: `rotate(${Math.random() * 360}deg)`,
          animation: `sprinkle-fall ${3 + Math.random() * 5}s linear ${Math.random() * 10}s infinite`,
        }} />
      );
    })}
    {/* Floating candy shapes */}
    {Array.from({ length: 8 }, (_, i) => {
      const shapes = ['circle', 'star', 'heart', 'diamond'];
      const shape = shapes[i % shapes.length];
      const colors = ['#EC4899', '#FBBF24', '#A78BFA', '#34D399', '#FB923C', '#3B82F6', '#EF4444', '#22D3EE'];
      const clipPaths = {
        circle: 'circle(50%)',
        star: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
        heart: 'polygon(50% 85%, 10% 40%, 0% 20%, 15% 0%, 35% 0%, 50% 20%, 65% 0%, 85% 0%, 100% 20%, 90% 40%)',
        diamond: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
      };
      return (
        <div key={i} className="absolute" style={{
          width: `${8 + Math.random() * 10}px`, height: `${8 + Math.random() * 10}px`,
          background: colors[i],
          clipPath: clipPaths[shape],
          top: `${10 + Math.random() * 70}%`, left: `${Math.random() * 100}%`,
          opacity: 0.25,
          animation: `candy-float ${6 + Math.random() * 6}s ease-in-out ${Math.random() * 8}s infinite`,
        }} />
      );
    })}
    {/* Lollipop silhouettes at bottom */}
    <svg className="absolute bottom-0 left-0 w-full h-[20%] opacity-[0.06]" preserveAspectRatio="none" viewBox="0 0 1440 200">
      {[120, 400, 720, 1050, 1320].map((x, i) => (
        <g key={i}>
          <line x1={x} y1="200" x2={x} y2={140 - i * 5} stroke="#EC4899" strokeWidth="4" />
          <circle cx={x} cy={130 - i * 5} r="20" fill={['#EC4899', '#FBBF24', '#A78BFA', '#34D399', '#FB923C'][i]} opacity="0.4" />
        </g>
      ))}
    </svg>
  </div>
);

/* Fireworks Spectacular, explosive bursts, trailing sparks, finale cascades */
const FireworksEffects = () => {
  const bursts = useMemo(() => Array.from({ length: 8 }, (_, i) => ({
    x: 5 + Math.random() * 90,
    y: 5 + Math.random() * 50,
    color: ["#EF4444", "#FBBF24", "#3B82F6", "#EC4899", "#22C55E", "#A78BFA", "#FB923C", "#22D3EE"][i],
    delay: i * 1.8,
    size: 100 + Math.random() * 80,
  })), []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
      {/* Firework sky glow */}
      <div className="absolute inset-0" style={{
        animation: "firework-sky-flash 14.4s ease-in-out infinite",
      }} />
      {bursts.map((b, i) => (
        <div key={i} className="absolute" style={{ left: `${b.x}%`, top: `${b.y}%` }}>
          {/* Central burst glow */}
          <div className="firework-burst" style={{
            width: b.size, height: b.size,
            background: `radial-gradient(circle, ${b.color}80, ${b.color}30, transparent 65%)`,
            animationDelay: `${b.delay}s`,
            boxShadow: `0 0 ${b.size}px ${b.color}50, 0 0 ${b.size / 2}px ${b.color}30`,
          }} />
          {/* Spark trails, 16 directions */}
          {Array.from({ length: 16 }, (_, j) => {
            const angle = (j / 16) * 360;
            return (
              <div key={j} className="firework-spark" style={{
                background: b.color,
                boxShadow: `0 0 6px ${b.color}, 0 0 12px ${b.color}80`,
                animationDelay: `${b.delay + 0.05}s`,
                '--angle': `${angle}deg`,
                '--dist': `${b.size * 0.7}px`,
              }} />
            );
          })}
          {/* Cascade sparkles */}
          {Array.from({ length: 8 }, (_, k) => (
            <div key={`cascade-${k}`} className="absolute rounded-full" style={{
              width: 2, height: 2,
              background: b.color,
              boxShadow: `0 0 4px ${b.color}`,
              animation: `fw-cascade ${1.5 + Math.random()}s ease-out ${b.delay + 0.8 + Math.random() * 0.5}s infinite`,
              '--cascade-x': `${(Math.random() - 0.5) * b.size * 0.8}px`,
            }} />
          ))}
        </div>
      ))}
      {/* Rising launch trails */}
      {Array.from({ length: 4 }, (_, i) => (
        <div key={`trail-${i}`} className="absolute" style={{
          width: 2, height: 20,
          background: `linear-gradient(0deg, rgba(255,255,255,0.6), transparent)`,
          bottom: '-5%',
          left: `${15 + i * 22}%`,
          animation: `fw-launch ${2.5}s ease-out ${i * 3.6}s infinite`,
        }} />
      ))}
    </div>
  );
};

/* Tropical Storm, heavy rain, dramatic lightning, churning waves, wind streaks */
const StormEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Wind streaks */}
    {Array.from({ length: 8 }, (_, i) => (
      <div key={`wind-${i}`} className="absolute" style={{
        width: `${60 + Math.random() * 120}px`, height: 1,
        background: `linear-gradient(90deg, transparent, rgba(148,163,184,0.15), transparent)`,
        top: `${10 + Math.random() * 70}%`, left: '-10%',
        animation: `wind-streak ${2 + Math.random() * 3}s linear ${Math.random() * 5}s infinite`,
      }} />
    ))}
    {/* Heavy rain */}
    {Array.from({ length: 60 }, (_, i) => (
      <div key={i} className="absolute" style={{
        width: 1, height: `${12 + Math.random() * 20}px`,
        background: "linear-gradient(180deg, transparent, rgba(148,163,184,0.5))",
        top: '-5%', left: `${Math.random() * 100}%`,
        transform: 'rotate(5deg)',
        animation: `rain-fall ${0.3 + Math.random() * 0.4}s linear ${Math.random() * 2}s infinite`,
      }} />
    ))}
    {/* Lightning flash */}
    <div className="absolute inset-0 lightning-flash" />
    {/* Churning waves */}
    <svg className="absolute bottom-0 left-0 w-full h-[25%]" preserveAspectRatio="none" viewBox="0 0 1440 200">
      <path fill="rgba(59,130,246,0.15)">
        <animate attributeName="d" dur="3s" repeatCount="indefinite"
          values="M0,100 Q120,60 240,100 Q360,140 480,100 Q600,60 720,100 Q840,140 960,100 Q1080,60 1200,100 Q1320,140 1440,100 L1440,200 L0,200Z;M0,120 Q120,80 240,120 Q360,160 480,120 Q600,80 720,120 Q840,160 960,120 Q1080,80 1200,120 Q1320,160 1440,120 L1440,200 L0,200Z;M0,100 Q120,60 240,100 Q360,140 480,100 Q600,60 720,100 Q840,140 960,100 Q1080,60 1200,100 Q1320,140 1440,100 L1440,200 L0,200Z" />
      </path>
      <path fill="rgba(14,165,233,0.1)">
        <animate attributeName="d" dur="4s" repeatCount="indefinite"
          values="M0,140 Q180,100 360,140 Q540,180 720,140 Q900,100 1080,140 Q1260,180 1440,140 L1440,200 L0,200Z;M0,120 Q180,80 360,120 Q540,160 720,120 Q900,80 1080,120 Q1260,160 1440,120 L1440,200 L0,200Z;M0,140 Q180,100 360,140 Q540,180 720,140 Q900,100 1080,140 Q1260,180 1440,140 L1440,200 L0,200Z" />
      </path>
    </svg>
    {/* Fog/mist layer */}
    <div className="absolute bottom-[15%] left-0 w-full h-[20%]" style={{
      background: "linear-gradient(0deg, rgba(148,163,184,0.08), transparent)",
      animation: "fog-drift 20s linear infinite",
    }} />
  </div>
);

/* Volcano, lava flow, erupting sparks, ash particles, magma glow */
const VolcanoEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Volcano silhouette */}
    <svg className="absolute bottom-0 left-0 w-full h-[45%]" preserveAspectRatio="none" viewBox="0 0 1440 400">
      <defs>
        <linearGradient id="lavaGlow" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(239,68,68,0.4)" />
          <stop offset="50%" stopColor="rgba(180,83,9,0.2)" />
          <stop offset="100%" stopColor="rgba(30,20,10,0.3)" />
        </linearGradient>
      </defs>
      <path d="M500,400 L600,100 L660,60 L680,80 L720,40 L760,80 L780,60 L840,100 L940,400Z" fill="rgba(50,30,20,0.3)" />
      <path d="M660,60 L680,80 L720,40 L760,80 L780,60" fill="none" stroke="rgba(239,68,68,0.4)" strokeWidth="3">
        <animate attributeName="stroke-opacity" values="0.2;0.6;0.2" dur="3s" repeatCount="indefinite" />
      </path>
      {/* Lava stream */}
      <path d="M710,60 Q720,150 700,250 Q680,350 690,400" fill="none" stroke="url(#lavaGlow)" strokeWidth="8" opacity="0.5">
        <animate attributeName="d" dur="5s" repeatCount="indefinite"
          values="M710,60 Q720,150 700,250 Q680,350 690,400;M710,60 Q700,150 720,250 Q740,350 730,400;M710,60 Q720,150 700,250 Q680,350 690,400" />
      </path>
    </svg>
    {/* Erupting lava sparks */}
    {Array.from({ length: 15 }, (_, i) => (
      <div key={i} className="absolute rounded-full" style={{
        width: `${3 + Math.random() * 5}px`, height: `${3 + Math.random() * 5}px`,
        background: ['#EF4444', '#F97316', '#FBBF24', '#FB923C'][i % 4],
        bottom: '60%', left: `${46 + Math.random() * 8}%`,
        boxShadow: `0 0 8px ${['#EF4444', '#F97316', '#FBBF24', '#FB923C'][i % 4]}`,
        animation: `lava-erupt ${2 + Math.random() * 3}s ease-out ${Math.random() * 4}s infinite`,
        '--erupt-x': `${(Math.random() - 0.5) * 200}px`,
        '--erupt-y': `${-80 - Math.random() * 120}px`,
      }} />
    ))}
    {/* Ash particles */}
    {Array.from({ length: 20 }, (_, i) => (
      <div key={`ash-${i}`} className="absolute rounded-full" style={{
        width: `${1 + Math.random() * 3}px`, height: `${1 + Math.random() * 3}px`,
        background: "rgba(120,100,80,0.3)",
        top: '-3%', left: `${Math.random() * 100}%`,
        animation: `ash-fall ${5 + Math.random() * 8}s linear ${Math.random() * 10}s infinite`,
      }} />
    ))}
    {/* Bottom lava glow */}
    <div className="absolute bottom-0 left-0 w-full h-[15%]" style={{
      background: "linear-gradient(0deg, rgba(239,68,68,0.15), rgba(251,146,60,0.08), transparent)",
      animation: "lava-glow-pulse 4s ease-in-out infinite",
    }} />
    {/* Heat distortion */}
    <div className="absolute bottom-[30%] left-[40%] w-[20%] h-[30%]" style={{
      background: "linear-gradient(0deg, rgba(239,68,68,0.08), transparent)",
      animation: "heat-shimmer 3s ease-in-out infinite",
    }} />
  </div>
);

/* Tropical Beach, gentle waves, coconut palms, flying seagulls, sand sparkle */
const BeachEffects = () => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
    {/* Sun rays */}
    <div className="absolute top-0 right-[15%] w-[200px] h-[200px]" style={{
      background: "radial-gradient(circle, rgba(251,191,36,0.2), rgba(251,146,60,0.1), transparent 70%)",
      animation: "sun-pulse 8s ease-in-out infinite",
    }} />
    {/* Palm tree silhouettes */}
    <svg className="absolute bottom-0 left-0 w-full h-[35%] opacity-[0.08]" preserveAspectRatio="none" viewBox="0 0 1440 300">
      {/* Palm 1 */}
      <line x1="200" y1="300" x2="210" y2="120" stroke="#065F46" strokeWidth="6" />
      <ellipse cx="240" cy="100" rx="60" ry="15" fill="#065F46" transform="rotate(-20 240 100)" />
      <ellipse cx="180" cy="110" rx="50" ry="12" fill="#065F46" transform="rotate(25 180 110)" />
      <ellipse cx="220" cy="90" rx="55" ry="13" fill="#065F46" transform="rotate(-45 220 90)" />
      {/* Palm 2 */}
      <line x1="1100" y1="300" x2="1090" y2="140" stroke="#065F46" strokeWidth="5" />
      <ellipse cx="1120" cy="120" rx="50" ry="12" fill="#065F46" transform="rotate(-15 1120 120)" />
      <ellipse cx="1060" cy="130" rx="45" ry="11" fill="#065F46" transform="rotate(30 1060 130)" />
    </svg>
    {/* Gentle waves */}
    <svg className="absolute bottom-0 left-0 w-full h-[20%]" preserveAspectRatio="none" viewBox="0 0 1440 200">
      <path fill="rgba(6,182,212,0.12)">
        <animate attributeName="d" dur="5s" repeatCount="indefinite"
          values="M0,130 Q240,100 480,130 Q720,160 960,130 Q1200,100 1440,130 L1440,200 L0,200Z;M0,140 Q240,110 480,140 Q720,170 960,140 Q1200,110 1440,140 L1440,200 L0,200Z;M0,130 Q240,100 480,130 Q720,160 960,130 Q1200,100 1440,130 L1440,200 L0,200Z" />
      </path>
      <path fill="rgba(14,165,233,0.08)">
        <animate attributeName="d" dur="7s" repeatCount="indefinite"
          values="M0,150 Q240,120 480,150 Q720,180 960,150 Q1200,120 1440,150 L1440,200 L0,200Z;M0,160 Q240,130 480,160 Q720,190 960,160 Q1200,130 1440,160 L1440,200 L0,200Z;M0,150 Q240,120 480,150 Q720,180 960,150 Q1200,120 1440,150 L1440,200 L0,200Z" />
      </path>
    </svg>
    {/* Sand sparkle at bottom */}
    <div className="absolute bottom-0 left-0 w-full h-[8%]" style={{
      background: "linear-gradient(0deg, rgba(251,191,36,0.1), transparent)",
    }} />
    {/* Flying seagulls */}
    {[20, 50, 75].map((x, i) => (
      <svg key={`gull-${i}`} className="absolute" style={{
        width: 20, height: 10,
        top: `${8 + i * 8}%`, left: `${x}%`,
        animation: `seagull-fly ${10 + i * 3}s linear ${i * 4}s infinite`,
        opacity: 0.2,
      }} viewBox="0 0 20 10">
        <path d="M0,8 Q5,2 10,6 Q15,2 20,8" fill="none" stroke="white" strokeWidth="1.5" />
      </svg>
    ))}
    {/* Floating coconuts / beach items */}
    {Array.from({ length: 5 }, (_, i) => (
      <div key={`sparkle-${i}`} className="absolute rounded-full" style={{
        width: 3, height: 3,
        background: 'rgba(251,191,36,0.4)',
        boxShadow: '0 0 6px rgba(251,191,36,0.3)',
        bottom: `${2 + Math.random() * 6}%`,
        left: `${Math.random() * 100}%`,
        animation: `sand-sparkle ${2 + Math.random() * 3}s ease-in-out ${Math.random() * 4}s infinite`,
      }} />
    ))}
  </div>
);

/* ====== DISPATCHER ====== */
const ThemeSpecialEffects = ({ themeSpecial }) => {
  if (!themeSpecial) return null;
  switch (themeSpecial) {
    case "bubbles": return <OceanEffects />;
    case "pyramids": return <PyramidEffects />;
    case "fireflies": return <ForestEffects />;
    case "aurora": return <AuroraEffects />;
    case "rainbow": return <CandyEffects />;
    case "fireworks": return <FireworksEffects />;
    case "storm": return <StormEffects />;
    case "volcano": return <VolcanoEffects />;
    case "beach": return <BeachEffects />;
    default: return null;
  }
};

export default ThemeSpecialEffects;
